import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { AssigneeStack } from './assignee-stack';
import { useEmployeeNameFormatter } from '@/lib/employee-name';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useAssignableMembers } from '@/hooks/use-boards';
import { useBoardSettings } from '@/hooks/use-tasks';
import { useAuthStore } from '@/stores/auth';
import { cn } from '@/lib/utils';
import { formatDate, toDateParam, parseDateOnly } from '@/lib/date';
import type { TaskPriority, TaskType, ProjectMemberItem } from '@hygge/shared';
import { Bug, Calendar, Check, Circle, Diamond, Star, User, UserCheck, X, Zap } from 'lucide-react';
import { Calendar as CalendarWidget } from '@/components/ui/calendar';
import { useMemo, useState } from 'react';

export const MODAL_WIDTH = 'max-w-[580px]';

export const TYPE_CONFIG: Record<TaskType, { label: string; icon: typeof Circle; color: string; bg: string; border: string }> = {
  task: { label: 'Task', icon: Circle, color: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/30' },
  bug: { label: 'Bug', icon: Bug, color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/30' },
  story: { label: 'Story', icon: Diamond, color: 'text-violet-400', bg: 'bg-violet-500/10', border: 'border-violet-500/30' },
  epic: { label: 'Epic', icon: Zap, color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/30' },
};

export const PRIORITY_CONFIG: Record<TaskPriority, { label: string; color: string; bars: number }> = {
  none: { label: 'None', color: 'text-muted-foreground', bars: 0 },
  low: { label: 'Low', color: 'text-blue-400', bars: 1 },
  medium: { label: 'Medium', color: 'text-yellow-400', bars: 2 },
  high: { label: 'High', color: 'text-orange-400', bars: 3 },
  urgent: { label: 'Urgent', color: 'text-red-400', bars: 4 },
};

export const ESTIMATE_OPTIONS = [1, 2, 3, 5, 8, 13, 21];


export function PriorityBars({ bars, className }: { bars: number; className?: string }) {
  return (
    <svg viewBox="0 0 16 16" className={cn('h-3.5 w-3.5', className)} fill="currentColor" aria-hidden="true">
      <rect x="1" y="11" width="2.5" height="4" rx="0.5" opacity={bars >= 1 ? 1 : 0.2} />
      <rect x="5" y="8" width="2.5" height="7" rx="0.5" opacity={bars >= 2 ? 1 : 0.2} />
      <rect x="9" y="5" width="2.5" height="10" rx="0.5" opacity={bars >= 3 ? 1 : 0.2} />
      <rect x="13" y="2" width="2.5" height="13" rx="0.5" opacity={bars >= 4 ? 1 : 0.2} />
    </svg>
  );
}

export function ChipButton({
  children,
  className,
  dashed,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { dashed?: boolean }) {
  return (
    <button
      type="button"
      className={cn(
        'inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs',
        'border transition-colors cursor-pointer',
        'text-muted-foreground hover:text-foreground hover:bg-accent',
        dashed ? 'border-dashed border-border' : 'border-border',
        className,
      )}
      {...props}
    >
      {children}
    </button>
  );
}

export function ChipSeparator() {
  return <div className="h-5 w-px bg-border mx-0.5" />;
}

// ─── Shared Assignee Chip ─────────────────────────────────────────────────

export function AssigneeChip({
  projectId,
  value,
  open,
  onToggle,
  onClose,
  onChange,
}: {
  projectId: string;
  /** Full assignee set; the FIRST id is the primary (mirrors `task.assignee`). */
  value: string[];
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
  /** Replace the whole set. Adding appends (keeps the first as primary). */
  onChange: (ids: string[]) => void;
}) {
  const formatName = useEmployeeNameFormatter();
  const members = useAssignableMembers(projectId);
  const myEmployeeId = useAuthStore((s) => s.user?.employee?.id);

  // Resolve the selected ids to people, preserving `value` order so the first
  // selected stays the primary in the stack.
  const selectedPeople = useMemo(
    () =>
      value
        .map((id) => members?.find((m: ProjectMemberItem) => m.employeeId === id))
        .filter((m): m is ProjectMemberItem => Boolean(m))
        .map((m) => ({
          id: m.employeeId,
          firstName: m.firstName,
          lastName: m.lastName,
          avatarUrl: m.avatarUrl,
        })),
    [members, value],
  );

  const isAssignedToMe = !!myEmployeeId && value.includes(myEmployeeId);
  const toggle = (id: string) =>
    onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);

  return (
    <Popover open={open} onOpenChange={(o) => (o ? onToggle() : onClose())}>
      <PopoverTrigger asChild>
        <ChipButton>
          {selectedPeople.length > 0 ? (
            <>
              <AssigneeStack people={selectedPeople} size="xs" max={3} />
              <span>
                {selectedPeople.length === 1
                  ? selectedPeople[0].firstName
                  : `${selectedPeople.length} assignees`}
              </span>
            </>
          ) : (
            <>
              <User className="h-3 w-3" />
              <span>Assignee</span>
            </>
          )}
        </ChipButton>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-0" align="start">
        <Command>
          <CommandInput placeholder="Search members..." className="h-8 text-xs" />
          <CommandList>
            <CommandEmpty className="py-3 text-xs text-center">No members found</CommandEmpty>
            <CommandGroup>
              {myEmployeeId && !isAssignedToMe && (
                <CommandItem
                  value="__assign_to_me__"
                  onSelect={() => toggle(myEmployeeId)}
                  className="text-xs text-primary"
                >
                  <UserCheck className="h-3.5 w-3.5 mr-2" />
                  Assign to me
                </CommandItem>
              )}
              {value.length > 0 && (
                <CommandItem
                  value="__clear__"
                  onSelect={() => onChange([])}
                  className="text-xs"
                >
                  <X className="h-3.5 w-3.5 mr-2 text-muted-foreground" />
                  Clear all
                </CommandItem>
              )}
              {members?.map((m: ProjectMemberItem) => (
                <CommandItem
                  key={m.employeeId}
                  // Both name orders in the search value (HW-731) so typing either
                  // "Ivan Petrov" or "Petrov Ivan" matches, whatever the display format.
                  value={`${m.firstName} ${m.lastName} ${m.lastName} ${m.firstName}`}
                  onSelect={() => toggle(m.employeeId)}
                  className="text-xs"
                >
                  <Avatar className="h-5 w-5 mr-2">
                    <AvatarImage src={m.avatarUrl || undefined} />
                    <AvatarFallback className="text-[7px]">{m.firstName[0]}{m.lastName[0]}</AvatarFallback>
                  </Avatar>
                  <span className="truncate">{formatName({ firstName: m.firstName, lastName: m.lastName })}</span>
                  <Check className={cn('ml-auto h-3.5 w-3.5', value.includes(m.employeeId) ? 'opacity-100' : 'opacity-0')} />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// ─── Shared Estimate Chip ─────────────────────────────────────────────────

export function EstimateChip({
  projectId,
  value,
  open,
  onToggle,
  onClose,
  onChange,
}: {
  projectId: string;
  value: number | null;
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
  onChange: (v: number | null) => void;
}) {
  const { data: settings } = useBoardSettings(projectId);
  const isHours = settings?.estimationUnit === 'hours';
  const [hoursInput, setHoursInput] = useState(value?.toString() ?? '');

  const label = value != null
    ? isHours ? `${value}h` : `${value} pts`
    : 'Estimate';

  const handleHoursSubmit = () => {
    const parsed = parseFloat(hoursInput);
    onChange(parsed > 0 ? parsed : null);
    onClose();
  };

  return (
    <Popover open={open} onOpenChange={(o) => {
      if (o) {
        setHoursInput(value?.toString() ?? '');
        onToggle();
      } else {
        onClose();
      }
    }}>
      <PopoverTrigger asChild>
        <ChipButton>
          <Star className="h-3 w-3 text-muted-foreground" />
          <span>{label}</span>
        </ChipButton>
      </PopoverTrigger>
      <PopoverContent className="w-52 p-3" align="start">
        {isHours ? (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Input
                type="number"
                value={hoursInput}
                onChange={(e) => setHoursInput(e.target.value)}
                placeholder="Hours..."
                className="h-8 text-sm"
                min={0}
                step={0.5}
                autoFocus
                onKeyDown={(e) => { if (e.key === 'Enter') handleHoursSubmit(); }}
              />
              <span className="text-xs text-muted-foreground shrink-0">hours</span>
            </div>
            <div className="flex items-center justify-between">
              {value != null && (
                <button
                  type="button"
                  className="text-xs text-muted-foreground hover:text-foreground cursor-pointer"
                  onClick={() => { onChange(null); onClose(); }}
                >
                  Clear
                </button>
              )}
              <button
                type="button"
                className="text-xs font-medium text-foreground hover:text-foreground/80 cursor-pointer ml-auto"
                onClick={handleHoursSubmit}
              >
                Apply
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-4 gap-1.5">
              {ESTIMATE_OPTIONS.map((n) => (
                <button
                  key={n}
                  type="button"
                  className={cn(
                    'h-8 rounded-md text-xs font-mono hover:bg-accent cursor-pointer transition-colors border border-border',
                    value === n && 'bg-primary text-primary-foreground border-primary hover:bg-primary/90',
                  )}
                  onClick={() => { onChange(n); onClose(); }}
                >
                  {n}
                </button>
              ))}
            </div>
            {value != null && (
              <button
                type="button"
                className="text-xs text-muted-foreground hover:text-foreground mt-2 cursor-pointer"
                onClick={() => { onChange(null); onClose(); }}
              >
                Clear estimate
              </button>
            )}
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}

// ─── Shared Due Date Chip ─────────────────────────────────────────────────

export function DueDateChip({
  value,
  open,
  onToggle,
  onClose,
  onChange,
}: {
  value: string | null;
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
  onChange: (v: string | null) => void;
}) {
  const dateObj = value ? parseDateOnly(value) : undefined;
  const [month, setMonth] = useState<Date | undefined>(dateObj ?? new Date());

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const isOverdue = dateObj && dateObj < today;

  const label = value
    ? formatDate(value, 'monthDay')
    : 'Due date';

  return (
    <Popover open={open} onOpenChange={(o) => (o ? onToggle() : onClose())}>
      <PopoverTrigger asChild>
        <ChipButton className={isOverdue ? 'text-red-400 border-red-500/30 bg-red-500/10' : undefined}>
          <Calendar className="h-3 w-3" />
          <span>{label}</span>
        </ChipButton>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <CalendarWidget
          mode="single"
          selected={dateObj}
          month={month}
          onMonthChange={setMonth}
          onSelect={(date) => {
            onChange(toDateParam(date));
            onClose();
          }}
          initialFocus
        />
        {value && (
          <div className="px-3 pb-2">
            <button
              type="button"
              className="text-xs text-muted-foreground hover:text-foreground cursor-pointer"
              onClick={() => { onChange(null); onClose(); }}
            >
              Clear due date
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
